import httpClient from "../http/http-client";
import { IEmissionDocument } from "../emission/emission.model";
import {
  CertificationHolderModelName,
  ICertificationCatalog,
  IManagementSystemCertification,
  IPostManagementSystemCertification,
} from "./management-system-certification.model";

// Formik initialises dates and optional strings to ''; the backend treats '' as "no value" for
// dates but a blank string would otherwise be stored verbatim, so both become null here.
const EMPTYABLE_FIELDS: (keyof IPostManagementSystemCertification)[] = [
  'certificateNumber', 'certificationBody', 'scopeDescription', 'issuedOn', 'expiresOn', 'nextAuditOn',
];

export function sanitiseCertificationPayload<T extends Partial<IPostManagementSystemCertification>>(payload: T): T {
  const cleaned: any = { ...payload }
  for (const field of EMPTYABLE_FIELDS) {
    if (cleaned[field] === '') cleaned[field] = null
  }
  return cleaned as T
}

export default class ManagementSystemCertificationService {
  http;

  constructor() {
    this.http = httpClient
  }

  async getCatalog(): Promise<ICertificationCatalog> {
    return this.http.get('management-system-certification/catalog')
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getAll(filter?: { holderModelName?: CertificationHolderModelName, holderObjectId?: string }): Promise<IManagementSystemCertification[]> {
    const params = new URLSearchParams()
    if (filter?.holderModelName) params.set('holderModelName', filter.holderModelName)
    if (filter?.holderObjectId) params.set('holderObjectId', filter.holderObjectId)
    const query = params.toString()
    return this.http.get(`management-system-certification${query ? `?${query}` : ''}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async create(payload: IPostManagementSystemCertification): Promise<IManagementSystemCertification> {
    return this.http.post('management-system-certification', sanitiseCertificationPayload(payload))
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async update(id: string, payload: Partial<IPostManagementSystemCertification>): Promise<IManagementSystemCertification> {
    return this.http.patch(`management-system-certification/${id}`, sanitiseCertificationPayload(payload))
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async remove(id: string): Promise<void> {
    return this.http.delete(`management-system-certification/${id}`)
      .then(() => undefined)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  async getDocuments(id: string): Promise<IEmissionDocument[]> {
    return this.http.get(`management-system-certification/${id}/documents`)
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  /** Uploads the certificate document(s); resolves to the refreshed document list. */
  async uploadDocuments(id: string, files: File[]): Promise<IEmissionDocument[]> {
    const form = new FormData()
    files.forEach(file => form.append('files', file))
    return this.http.post(`management-system-certification/${id}/documents`, form)
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }

  /** Soft-deletes the document row only; the stored file is retained. */
  async removeDocument(id: string, resourceId: string): Promise<IEmissionDocument[]> {
    return this.http.delete(`management-system-certification/${id}/documents/${resourceId}`)
      .then(response => response.data)
      .catch(err => {
        console.log(err)
        throw err
      });
  }
}
